import { type Ref, ref, watch } from 'vue'

import { useDesignStore } from '@/stores/design'

/** Resolved render URLs plus the loading flag every design-page surface renders from. */
export interface UseDesignRenderUrls {
  urls: Ref<(string | null)[]>
  loading: Ref<boolean>
}

/** Resolves a design's rendered page URLs from `stores/design.ts`, for one render version, guarding against a stale fetch landing after a newer design, version or page count. */
export function useDesignRenderUrls(
  designId: Ref<string | null>,
  renderVersion: Ref<number | null>,
  pageCount: Ref<number>,
): UseDesignRenderUrls {
  const store = useDesignStore()
  const urls = ref<(string | null)[]>([])
  const loading = ref(false)
  let requestToken = 0

  async function fetch(): Promise<void> {
    const id = designId.value
    const count = pageCount.value
    const token = ++requestToken
    if (!id || count < 1) {
      urls.value = []
      loading.value = false
      return
    }
    loading.value = true
    const resolved = await store.renderUrlsFor(id, renderVersion.value, count)
    if (token !== requestToken) return
    urls.value = resolved
    loading.value = false
  }

  watch([designId, renderVersion, pageCount], () => void fetch(), { immediate: true })

  return { urls, loading }
}
